/**
 * `soundOnlyTurn.ts` — is a spoken turn's transcript a SOUND rather than words?
 * Web-only; Unity's `RealtimeSession.cs` answers every committed turn.
 *
 * A learner thinking out loud ("Hm.", "Um, uh.") or a mic turn the transcriber
 * made nothing of (".", "") asks nothing, so Spark says nothing to it
 * (`RealtimeSession` cancels the reply before it is heard, and the chat never
 * shows it). `roles/guide.md` already asks for that silence ("A sound or a
 * fragment is a SILENT turn") and the model mostly obeys, but not reliably: the
 * 2026-10-08 trace answered a "Hm." with "Take your time and follow the
 * instructions on screen. If anything seems unclear, just ask." The transcript
 * arrives 300–600 ms before the reply's first audio, which is what makes
 * deciding it here possible at all.
 *
 * Two kinds of sound, and they are not treated alike:
 *   - **Hesitation** — hm, um, uh, er, ah, eh, or no words at all. It never
 *     answers anything, so it is always silent.
 *   - **Yes/no sounds** — "Mm-hmm", "Uh-huh", "Mhm", "Mm", "Uh-uh", "Aha". They answer
 *     a question and nothing else, so they are silent unless Spark's last line
 *     asked one (user direction, 2026-10-08: "yes/no sounds have to be answered
 *     only when Spark has just asked a question"). Without a question they are
 *     the learner acknowledging, which is a hum with a different vowel.
 *
 * Never a sound, whatever its letters: anything asked as a question ("Hm?",
 * "Huh?" — "say that again"), and anything with a word in it ("Hmm, okay",
 * "Oh"). A sentence that trails off, or a mis-heard noise the transcriber turned
 * into a word ("Bing.", "tree."), is not decidable from text and stays the
 * prompt's.
 *
 * ⚠ **Not the same lists as `stepMoveWords.ts`.** That one decides only whether
 * a turn could ask for a step move, so it lumps every murmur together as filler
 * and reads "mhm" / "uh huh" / "mm hmm" as a yes only after a move question.
 */

/** Hesitation sounds, repeated letters included: hm/hmm, um/umm/uhm, uh/uhh, er/erm, ah/ahh, eh. */
const K_HESITATION_TOKEN = /^(?:h+m+|u+h*m+|u+h+|e+r+m*|a+h+|e+h+)$/;

/**
 * A yes/no sound, with hyphens and spaces taken out first: mm, mmm, mhm, mmhm,
 * mmhmm ("Mm-hmm"), uhhuh ("Uh-huh"), uhuh ("Uh-uh"), aha. ⚠ `gpt-4o-transcribe`
 * wrote a clearly spoken "Uh-huh." as "Aha." 3 times in 3 (live, 2026-10-08),
 * so "aha" is in the set for the transcriber's sake as much as the speaker's.
 */
const K_YES_NO_SOUND = /^(?:m+h*m+|u+h+h*u+h+|a+h+a+h*)$/;

function isAskedAsQuestion(text: string): boolean {
  return text.endsWith("?");
}

/**
 * True when `transcript` holds no words at all, or nothing but hesitation
 * sounds — and is not asked as a question.
 */
export function isSoundOnlyTranscript(transcript: string): boolean {
  const text = transcript.trim();
  if (!/[\p{L}\p{N}]/u.test(text)) return true;
  if (isAskedAsQuestion(text)) return false;
  // Hyphens stay inside a token, so "uh-huh" and "mm-hmm" never split into two
  // hesitation sounds.
  const tokens = text
    .toLowerCase()
    .split(/[^\p{L}\p{N}-]+/u)
    .filter((token) => token.length > 0);
  return tokens.length > 0 && tokens.every((token) => K_HESITATION_TOKEN.test(token));
}

/**
 * True when `transcript` is nothing but a yes/no sound ("Mm-hmm.", "Uh-huh",
 * "Mhm") — and is not asked as a question.
 */
export function isYesNoSoundTranscript(transcript: string): boolean {
  const text = transcript.trim();
  if (isAskedAsQuestion(text)) return false;
  const letters = text.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, "");
  return letters.length > 0 && K_YES_NO_SOUND.test(letters);
}

/**
 * Whether a line of Spark's ASKS the learner something — its last sentence is
 * a question ("Ready to move on?", a step's "Are drones operating in your work
 * area?"). A question earlier in a line that then goes on answering it is not.
 */
export function endsWithQuestion(line: string): boolean {
  return /\?["'”’)\]]*$/.test(line.trim());
}
